import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/utils/cn';

export interface ComboboxOption {
  id: string;
  label: string;
}

interface Props {
  value: string | null;
  onChange: (id: string | null) => void;
  options: ComboboxOption[];
  /** Текст пустого пункта («— не выбран —»). Не задан — пустой выбор недоступен. */
  emptyLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

/** Выпадающий список с поиском по подстроке — замена <select> для длинных списков. */
export function Combobox({ value, onChange, options, emptyLabel, placeholder, disabled, className }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const selected = useMemo(() => options.find((o) => o.id === value) ?? null, [options, value]);

  // Пустой пункт держим первым и не фильтруем — он всегда должен быть доступен
  const items = useMemo<ComboboxOption[]>(() => {
    const q = query.trim().toLocaleLowerCase('ru');
    const found = q ? options.filter((o) => o.label.toLocaleLowerCase('ru').includes(q)) : options;
    return emptyLabel != null ? [{ id: '', label: emptyLabel }, ...found] : found;
  }, [options, query, emptyLabel]);

  const measure = () => {
    const el = wrapRef.current;
    if (el) setRect(el.getBoundingClientRect());
  };

  useLayoutEffect(() => { if (open) measure(); }, [open]);

  useEffect(() => {
    if (!open) return;
    // capture: список лежит в прокручиваемом теле модалки, ловим её скролл тоже
    const onScroll = () => measure();
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!wrapRef.current?.contains(t) && !menuRef.current?.contains(t)) close();
    };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  const close = () => { setOpen(false); setQuery(''); };

  const pick = (opt: ComboboxOption) => {
    onChange(opt.id === '' ? null : opt.id);
    close();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    // Escape при открытом списке гасим: иначе он же закроет саму модалку
    if (e.key === 'Escape') { if (open) { e.stopPropagation(); close(); } return; }
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) { setOpen(true); setActive(0); e.preventDefault(); return; }
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, items.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); const it = items[active]; if (it) pick(it); }
    else if (e.key === 'Tab') close();
  };

  return (
    <div ref={wrapRef} className={cn('relative', className)}>
      <input
        ref={inputRef}
        className={cn('input pr-8', disabled && 'opacity-60 cursor-not-allowed')}
        disabled={disabled}
        value={open ? query : (selected?.label ?? '')}
        placeholder={open ? (selected?.label ?? placeholder ?? '') : (placeholder ?? emptyLabel ?? '')}
        onChange={(e) => { setQuery(e.target.value); setActive(0); if (!open) setOpen(true); }}
        onFocus={() => { if (!disabled) { setOpen(true); setActive(0); } }}
        onKeyDown={onKeyDown}
        autoComplete="off"
      />
      <ChevronDown className="w-4 h-4 absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-3 pointer-events-none" />

      {open && rect && createPortal(
        <div
          ref={menuRef}
          className="menu fixed z-[120] max-h-64 overflow-y-auto"
          style={{ left: rect.left, top: rect.bottom + 4, width: rect.width }}
        >
          {items.length === 0 && <div className="px-3 py-2 text-ink-3">Ничего не найдено</div>}
          {items.map((o, i) => (
            <div
              key={o.id || '__empty'}
              className={cn('menu-item', i === active && 'bg-surface-2', o.id === (value ?? '') && 'font-semibold')}
              onMouseEnter={() => setActive(i)}
              // click теряется из-за закрытия по mousedown снаружи — выбираем на mousedown
              onMouseDown={(e) => { e.preventDefault(); pick(o); }}
            >
              <span className="flex-1 truncate">{o.label}</span>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
